import React, { type PropsWithChildren } from 'react';
import AtlaskitButton, { type Appearance } from '@atlaskit/button';
import * as Styled from './styled';
import { FormattedMessage } from 'react-intl-next';
import messages from './messages';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	ButtonActionType,
	type ButtonProps,
	ChevronDirection,
	type ChevronProps,
	type NavigationBulletProps,
	type NavigationBulletGroupProps,
} from './types';
import ChevronLeftCircleIcon from '@atlaskit/icon/glyph/chevron-left-circle';
import ChevronRightCircleIcon from '@atlaskit/icon/glyph/chevron-right-circle';

const NavigationBullet: React.FC<NavigationBulletProps> = ({
	position,
	activePosition,
	onNavigationBulletClick,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleNavigationBulletClick = () => {
		const event = createAnalyticsEvent({
			actionSubject: 'provisioningModalNavigationBullet',
			action: 'clicked',
			attributes: { position },
		});
		fireUIAnalytics(event);
		onNavigationBulletClick(position);
	};
	return (
		<Styled.NavigationBullet
			onClick={handleNavigationBulletClick}
			isActive={activePosition === position}
		/>
	);
};

const NavigationBulletGroup: React.FC<NavigationBulletGroupProps> = ({
	count,
	activePosition,
	onNavigationBulletClick,
}) => {
	if (count < 2) {
		return null;
	}
	const navigationBullets = Array(count).fill(0);

	return (
		<Styled.NavigationBulletsWrapper>
			{navigationBullets.map((_, index) => (
				<NavigationBullet
					activePosition={activePosition}
					position={index + 1}
					onNavigationBulletClick={onNavigationBulletClick}
				/>
			))}
		</Styled.NavigationBulletsWrapper>
	);
};

const buttonAppearance: { [K in ButtonActionType]: Appearance } = {
	[ButtonActionType.DISMISS]: 'subtle',
	[ButtonActionType.NEXT]: 'primary',
	[ButtonActionType.DONE]: 'primary',
};

const Button: React.FC<ButtonProps> = ({ onClick, buttonActionType }) => {
	const handleClick = (_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'provisioningModalControl', {
			buttonActionType,
		});
		onClick();
	};
	return (
		<Styled.ButtonWrapper>
			<AtlaskitButton appearance={buttonAppearance[buttonActionType]} onClick={handleClick}>
				<FormattedMessage {...messages[buttonActionType]} />
			</AtlaskitButton>
		</Styled.ButtonWrapper>
	);
};

const Container = ({ children }: PropsWithChildren<{}>) => (
	<Styled.ControlsWrapper>{children}</Styled.ControlsWrapper>
);

const ChevronIconTypes = {
	// TODO: Migrate to new icon DSP-20773
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	[ChevronDirection.RIGHT]: ChevronRightCircleIcon,
	// TODO: Migrate to new icon DSP-20773
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	[ChevronDirection.LEFT]: ChevronLeftCircleIcon,
};
const Chevron: React.FC<ChevronProps> = ({ onPrevious, direction, onNext }) => {
	const handleClick = () => {
		if (direction === ChevronDirection.LEFT && onPrevious) {
			onPrevious();
		}
		if (direction === ChevronDirection.RIGHT && onNext) {
			onNext();
		}
	};
	const ChevronIcon = ChevronIconTypes[direction];
	return (
		<Styled.ChevronWrapper direction={direction} onClick={handleClick}>
			<ChevronIcon size="large" label={direction} />
		</Styled.ChevronWrapper>
	);
};

export default { Button, NavigationBulletGroup, Container, Chevron };
